import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { IdbService } from './idb.service';
import { NetworkService } from './network.service';
import { CacheStore } from './cache-store.service';

/** Événement émis quand une mutation a été rejouée avec succès côté serveur. */
export interface MutationReplayedEvent {
  mutationId: number;
  type: string;
  label?: string;
  method: string;
  url: string;
}

/** Statut d'une mutation dans la queue. */
export type MutationStatus = 'PENDING' | 'RETRYING' | 'FAILED' | 'DONE';

/**
 * Représentation persistée d'une écriture HTTP en attente.
 *
 * <p>Chaque entrée est une réécriture fidèle de la requête originale
 * (méthode, url, body, headers) plus des métadonnées pour le replay :
 * statut, nombre de tentatives, dernier message d'erreur, etc.</p>
 */
export interface PendingMutation {
  /** Clé primaire IDB (auto-incrément). */
  id?: number;
  /** Méthode HTTP. */
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** URL complète. */
  url: string;
  /** Body sérialisé (peut être null pour DELETE). */
  body: unknown | null;
  /** Sous-ensemble des headers à reposter (Authorization sera réinjecté par l'interceptor). */
  headers: Record<string, string>;
  /** Catégorie logique pour stats / invalidation (ex: "client"). */
  type: string;
  /** Statut courant. */
  status: MutationStatus;
  /** Date de création (ms epoch). */
  createdAt: number;
  /** Nombre de tentatives effectuées. */
  attempts: number;
  /** Dernier message d'erreur si en échec. */
  lastError?: string;
  /** Libellé humain pour la page Sync (ex: "Création client Aminatou D."). */
  label?: string;
}

/**
 * File d'attente persistante des mutations effectuées en mode offline.
 *
 * <p>Au Niger, où la connectivité saute fréquemment au milieu d'une
 * session de travail, le pire scénario est qu'un atelier crée une commande,
 * voie une erreur, refasse, et retrouve 3 doublons quand le réseau revient.</p>
 *
 * <p>Cette queue résout le problème : toute écriture émise en offline est
 * mise en file persistante (IDB) avec un libellé humain, et rejouée
 * automatiquement dès que le {@link NetworkService} signale le retour de
 * connexion. L'utilisateur voit "en attente de sync" plutôt qu'une erreur,
 * et il n'y a pas de doublon.</p>
 */
@Injectable({ providedIn: 'root' })
export class MutationQueue {
  private idb = inject(IdbService);
  private http = inject(HttpClient);
  private network = inject(NetworkService);
  private cache = inject(CacheStore);

  /** Compteur courant pour les vues qui s'abonnent. */
  private readonly _pending$ = new BehaviorSubject<number>(0);
  readonly pending$ = this._pending$.asObservable();

  /** Statut courant de la queue. */
  private readonly _flushing$ = new BehaviorSubject<boolean>(false);
  readonly flushing$ = this._flushing$.asObservable();

  /** Événement émis à chaque mutation rejouée avec succès — pour toast de confirmation. */
  private readonly _replayed$ = new Subject<MutationReplayedEvent>();
  readonly replayed$ = this._replayed$.asObservable();

  /** Événement émis quand la file vient d'être totalement vidée (toutes synchronisées). */
  private readonly _drained$ = new Subject<number>();
  readonly drained$ = this._drained$.asObservable();

  /** Nombre max de retries avant de marquer FAILED. */
  private readonly MAX_ATTEMPTS = 6;

  constructor() {
    // Au démarrage : compter ce qui est déjà en attente + s'inscrire au retour réseau.
    this.refreshCount();
    this.network.online$.subscribe((online) => {
      if (online) this.flush();
    });
  }

  /** Met une mutation en queue (appel par l'intercepteur HTTP). */
  async enqueue(mut: Omit<PendingMutation, 'id' | 'status' | 'createdAt' | 'attempts'>): Promise<number> {
    const entry: PendingMutation = {
      ...mut,
      status: 'PENDING',
      createdAt: Date.now(),
      attempts: 0,
    };
    const id = (await this.idb.put(IdbService.STORE_MUTATIONS, entry)) as number;
    await this.refreshCount();
    return id;
  }

  /** Liste toutes les mutations (pour la page Sync). */
  async list(): Promise<PendingMutation[]> {
    return this.idb.getAll<PendingMutation>(IdbService.STORE_MUTATIONS);
  }

  /** Nombre d'éléments encore PENDING ou RETRYING. */
  async pendingCount(): Promise<number> {
    const all = await this.list();
    return all.filter((m) => m.status === 'PENDING' || m.status === 'RETRYING').length;
  }

  /** Supprime une mutation (abandon volontaire). */
  async remove(id: number): Promise<void> {
    await this.idb.delete(IdbService.STORE_MUTATIONS, id);
    await this.refreshCount();
  }

  /** Vide la queue. */
  async clear(): Promise<void> {
    await this.idb.clear(IdbService.STORE_MUTATIONS);
    await this.refreshCount();
  }

  /**
   * Rejoue toutes les mutations en attente.
   *
   * <p>Idempotent côté queue : si déjà en cours, retourne immédiatement.
   * Idempotent côté backend dépend des endpoints ; pour les POST critiques
   * (paiement) il faudra ajouter un Idempotency-Key (à venir).</p>
   */
  async flush(): Promise<void> {
    if (this._flushing$.value) return;
    if (!this.network.isOnline()) return;

    this._flushing$.next(true);
    let replayedCount = 0;
    try {
      const all = (await this.list()).sort((a, b) => a.createdAt - b.createdAt);
      for (const mut of all) {
        if (mut.status === 'DONE' || mut.status === 'FAILED') continue;
        if (!this.network.isOnline()) break; // déconnexion en cours de replay
        const success = await this.replayOne(mut);
        if (success) replayedCount++;
      }
    } finally {
      this._flushing$.next(false);
      await this.refreshCount();
      if (replayedCount > 0 && this._pending$.value === 0) {
        this._drained$.next(replayedCount);
      }
    }
  }

  /** Force le replay d'une mutation en échec. */
  async retry(id: number): Promise<void> {
    const mut = await this.idb.get<PendingMutation>(IdbService.STORE_MUTATIONS, id);
    if (!mut) return;
    mut.status = 'RETRYING';
    mut.attempts = 0;
    mut.lastError = undefined;
    await this.idb.put(IdbService.STORE_MUTATIONS, mut);
    await this.refreshCount();
    if (this.network.isOnline()) await this.flush();
  }

  // ─── interne ──────────────────────────────────────────────────────────

  /**
   * Rejoue une mutation. Retourne true si elle a été envoyée avec succès au
   * backend (et donc supprimée de la queue), false sinon (toujours en queue
   * pour un retry ultérieur).
   */
  private async replayOne(mut: PendingMutation): Promise<boolean> {
    mut.status = 'RETRYING';
    mut.attempts++;
    await this.idb.put(IdbService.STORE_MUTATIONS, mut);

    const headers = new HttpHeaders({
      ...mut.headers,
      // X-Offline-Skip : bypass complet de l'offlineInterceptor (sinon le replay
      //   serait re-mis en queue en cas d'échec réseau → boucle).
      // X-Offline-Replay : marqueur informationnel pour debug / metrics serveur.
      // X-Skip-Error-Toast : on ne veut pas spammer l'utilisateur de toasts
      //   d'erreur pour chaque tentative de replay — l'état est déjà visible
      //   sur la page Sync.
      // X-Idempotency-Key : idempotence basique pour éviter les doublons
      //   côté backend si la même requête rejouée plusieurs fois.
      'X-Offline-Skip': '1',
      'X-Offline-Replay': '1',
      'X-Skip-Error-Toast': 'true',
      'X-Idempotency-Key': `mut-${mut.id}-${mut.createdAt}`,
    });

    try {
      switch (mut.method) {
        case 'POST':
          await firstValueFrom(this.http.post(mut.url, mut.body ?? {}, { headers }));
          break;
        case 'PUT':
          await firstValueFrom(this.http.put(mut.url, mut.body ?? {}, { headers }));
          break;
        case 'PATCH':
          await firstValueFrom(this.http.patch(mut.url, mut.body ?? {}, { headers }));
          break;
        case 'DELETE':
          await firstValueFrom(this.http.delete(mut.url, { headers }));
          break;
      }
      // Succès — on supprime de la queue + invalide le cache du type concerné.
      await this.idb.delete(IdbService.STORE_MUTATIONS, mut.id!);
      await this.cache.invalidateByType(mut.type);
      this._replayed$.next({
        mutationId: mut.id!,
        type: mut.type,
        label: mut.label,
        method: mut.method,
        url: mut.url,
      });
      return true;
    } catch (err: any) {
      const msg = this.extractError(err);
      // Si erreur "réseau" (status 0), on laisse en RETRYING — sera repris au prochain online.
      const isNetwork = err?.status === 0 || err?.name === 'TimeoutError'
              || /timeout|network/i.test(String(err?.message || ''));
      if (isNetwork) {
        this.network.markOffline();
        mut.status = 'PENDING';
        mut.lastError = 'Réseau indisponible';
        await this.idb.put(IdbService.STORE_MUTATIONS, mut);
        return false;
      }
      // Erreur côté serveur — on retry jusqu'à MAX_ATTEMPTS puis FAILED.
      if (mut.attempts >= this.MAX_ATTEMPTS) {
        mut.status = 'FAILED';
      } else {
        mut.status = 'PENDING';
      }
      mut.lastError = msg;
      await this.idb.put(IdbService.STORE_MUTATIONS, mut);
      return false;
    }
  }

  private async refreshCount(): Promise<void> {
    const count = await this.pendingCount();
    this._pending$.next(count);
  }

  private extractError(err: any): string {
    if (!err) return 'Erreur inconnue';
    if (err.error?.message) return String(err.error.message);
    if (err.message) return String(err.message);
    return JSON.stringify(err).substring(0, 200);
  }
}
